// Worlds: the ones you play, the ones you can join, the ones that ended. Joining asks for a starting direction.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { WorldDetails } from "@/api/types";
import { Page } from "@/app/Page";
import { art } from "@/kit/art";
import { IconGlobe } from "@/kit/icons";
import { Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Empty, Medallion, Row, RowText, Skeleton } from "@/kit/primitives";
import { keys, presenceVillages, useInit, worldDetails } from "@/state/queries";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { fmtNum } from "@/game/format";

// world-process WorldPopulator: 0 = south-west quadrant, 1 = south-east, 2 = north-west, 3 = north-east, 4 = the least crowded
const DIRECTIONS = [
  { key: 2, label: "North-west", hint: "Top left of the map" },
  { key: 3, label: "North-east", hint: "Top right of the map" },
  { key: 0, label: "South-west", hint: "Bottom left of the map" },
  { key: 1, label: "South-east", hint: "Bottom right of the map" },
  { key: 4, label: "Anywhere", hint: "The least crowded corner" },
];

function detailLine(d?: WorldDetails): string {
  if (!d) return "";
  const parts = [
    d["World Speed"] ? `${d["World Speed"]}× speed` : null,
    d["Unit Speed"] ? `${d["Unit Speed"]}× units` : null,
    d.Population ? `${typeof d.Population === "number" ? fmtNum(d.Population) : d.Population} players` : null,
    d.Research ? `${d.Research} research` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

export function Worlds() {
  const init = useInit();
  const nav = useNavigate();
  const qc = useQueryClient();
  const setWorld = useSession((s) => s.setWorld);
  const [joining, setJoining] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const prompts = usePrompts();

  const w = init.data?.world;
  const names = init.data?.config?.world_names ?? {};
  const lists = useMemo(() => ({ inW: w?.worlds_in ?? [], notIn: w?.worlds_not_in ?? [], ended: w?.worlds_ended ?? [] }), [w]);

  const join = async (direction: number) => {
    if (!joining) return;
    setBusy(true);
    try {
      await api.world.join({ wid: joining }, String(direction));
      prompts.toast(`Welcome to World ${joining}.`, "success");
      await qc.invalidateQueries({ queryKey: keys.init });
      setWorld(joining);
      setJoining(null);
      nav(`/w/${joining}`);
    } catch (e) {
      prompts.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page layout="col" header={{ kind: "title", title: "WORLDS", subtitle: "Choose your battlefield" }}>
      <div className="stack">
        {init.isLoading && <><Skeleton h={160} /><Skeleton h={160} /></>}
        {init.isError && <Card><Empty>Couldn't read your worlds. Pull down to try again.</Empty></Card>}
        {w && (
          <>
            <Card>
              <CardHead title="Your worlds" aside={`${lists.inW.length} ${lists.inW.length === 1 ? "world" : "worlds"}`} />
              <div className="rows">
                {lists.inW.map((wid) => {
                  const d = worldDetails(w, wid);
                  const cities = presenceVillages(w, wid).length;
                  return (
                    <Row key={wid} as="button" onClick={() => { setWorld(wid); nav(`/w/${wid}`); }}>
                      <Medallion><IconGlobe /></Medallion>
                      <RowText
                        eyebrow={`World ${wid}${names[String(wid)] ? ` · ${names[String(wid)]}` : ""}`}
                        line={<>{cities ? <><b>{cities}</b> {cities === 1 ? "city" : "cities"}</> : "Your empire"}</>}
                        meta={detailLine(d)}
                      />
                      <div className="right"><Button size="sm">Enter</Button></div>
                    </Row>
                  );
                })}
                {!lists.inW.length && <Empty>You haven't joined a world yet. Pick one below.</Empty>}
              </div>
            </Card>
            <Card>
              <CardHead title="Open worlds" aside={`${lists.notIn.length} to join`} />
              <div className="rows">
                {lists.notIn.map((wid) => {
                  const d = worldDetails(w, wid);
                  return (
                    <Row key={wid}>
                      <Medallion src={art.nav.map} alt="" />
                      <RowText eyebrow={`World ${wid}${names[String(wid)] ? ` · ${names[String(wid)]}` : ""}`} line={d ? <>Beginner's protection <b>{d["Beginner's Protection"]}</b></> : "Open"} meta={detailLine(d)} />
                      <div className="right"><Button size="sm" kind="crimson" onClick={() => setJoining(wid)}>Join</Button></div>
                    </Row>
                  );
                })}
                {!lists.notIn.length && <Empty>No world is open for new players right now.</Empty>}
              </div>
            </Card>
            {lists.ended.length > 0 && (
              <Card small>
                <CardHead title="Ended" aside={`${lists.ended.length}`} />
                <div className="rows">
                  {lists.ended.map((wid) => (
                    <Row key={wid} sub>
                      <RowText line={`World ${wid}${names[String(wid)] ? ` · ${names[String(wid)]}` : ""}`} meta="This world has ended." />
                    </Row>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
      <Sheet open={joining !== null} onClose={() => !busy && setJoining(null)} label="Join world" title={<h3 className="eyebrow" style={{ margin: "0 0 6px" }}>Join World {joining}</h3>}>
        <p className="hint" style={{ margin: "0 0 16px", fontSize: "var(--fs-body2)" }}>Where should your first city be? You can't move it later, but you can conquer more.</p>
        <div className="rows">
          {DIRECTIONS.map((d) => (
            <Row key={d.key} as="button" onClick={() => join(d.key)} disabled={busy}>
              <RowText line={d.label} meta={d.hint} />
              <div className="right"><Button size="sm" busy={busy}>Start here</Button></div>
            </Row>
          ))}
        </div>
      </Sheet>
    </Page>
  );
}
